import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "../css/pages/landing.css";
import {ReactComponent as LogoSvg} from '../assets/check2_logo.svg';
import Slide1 from "../assets/slide1.png";
import Slide2 from "../assets/slide2.png";
import Slide3 from "../assets/slide3.png";
import Slide4 from "../assets/slide4.png";
import Carousel from "./components/Carousel";
import { ReactComponent as Step1 } from '../assets/step1.svg';
import { ReactComponent as Step2 } from '../assets/step2.svg';
import { ReactComponent as Step3 } from '../assets/step3.svg';
import { ReactComponent as Step4 } from '../assets/step4.svg';

// const Check2Logo = ({ size = 34 }) => (
//   <div className="c2-logo" style={{ width: size, height: size }}>
//     <div className="c2-logo_dot" />
//     <div className="c2-logo_mark">
//       <LogoSvg />
//     </div>
//   </div>
// );

const Stat = ({ label, value }) => (
  <div className="stat">
    <div className="stat_value">{value}</div>
    <div className="stat_label">{label}</div>
  </div>
);

const slides = [
  { src: Slide2, alt: "check² screen 2" },
  { src: Slide1, alt: "check² screen 1" },
  { src: Slide3, alt: "check² screen 3" },
  { src: Slide4, alt: "check² screen 4" },
];

const FeatureCard = ({ icon, title, desc }) => (
  <div className="card feature-card">
    <div className="feature-card_icon" aria-hidden>
      {icon}
    </div>
    <div className="feature-card_body">
      <div className="feature-card_title">{title}</div>
      <div className="feature-card_desc">{desc}</div>
    </div>
  </div>
);

const Step = ({ icon, title, desc }) => (
  <div className="step">
    <div className="step_idx">{icon}</div>
    <div className="step_content">
      <div className="step_title">{title}</div>
      <div className="step_desc">{desc}</div>
    </div>
  </div>
);

const FloatingChip = ({ children, style }) => (
  <div className="floating-chip" style={style}>
    {children}
  </div>
);

function useTyping(words = [], speedMs = 70, pauseMs = 900) {
  const [i, setI] = useState(0);
  const [sub, setSub] = useState(0);
  const [dir, setDir] = useState(1);

  const word = words[i] ?? "";

  useEffect(() => {
    if (!words.length) return;

    const doneTyping = sub >= word.length && dir === 1;
    const doneDeleting = sub <= 0 && dir === -1;

    const t = setTimeout(
      () => {
        if (doneTyping) {
          setDir(-1);
        } else if (doneDeleting) {
          setDir(1);
          setI((prev) => (prev + 1) % words.length);
        } else {
          setSub((prev) => prev + dir);
        }
      },
      doneTyping ? pauseMs : speedMs,
    );

    return () => clearTimeout(t);
  }, [words, i, sub, dir, word, speedMs, pauseMs]);

  return word.slice(0, sub);
}

export default function LandingPage() {
  const typed = useTyping(
    [
      "Compliant Onboarding",
      "Risk Decisions",
      "Client Verification",
      "Financial Services"
    ],
    60,
    800,
  );

  const year = useMemo(() => new Date().getFullYear(), []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="landing">
      {/* Decorative background */}
      <div className="bg">
        <div className="bg_grid" />
        <div className="bg_orb bg_orb--a" />
        <div className="bg_orb bg_orb--b" />
        <div className="bg_orb bg_orb--c" />
      </div>

      {/* Top Nav */}
      <header className="nav">
        <div className="container nav_inner">
          <div
            className="nav_left"
            onClick={() => scrollTo("top")}
            role="button"
            tabIndex={0}
          >
            <LogoSvg />
            {/* <div className="nav_brand">
              <div className="nav_name">
                check<span className="nav_nameSup">²</span>
              </div>
              <div className="nav_tag">AML + ID Verification</div>
            </div> */}
          </div>

          <nav className="nav_links">
            <button className="link" onClick={() => scrollTo("features")}>
              Features
            </button>
            <button className="link" onClick={() => scrollTo("how")}>
              How it works
            </button>
            {/* <button className="link" onClick={() => scrollTo("security")}>
              Security
            </button> */}
            <button className="link" onClick={() => scrollTo("cta")}>
              Get started
            </button>
          </nav>

          <div className="nav_actions">
            <button className="btn btn--ghost" onClick={() => scrollTo("how")}>
              View flow
            </button>
            <button
              className="btn btn--primary"
              onClick={() => scrollTo("cta")}
            >
              Start a check
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <main id="top" className="hero">
        <div className="container hero_grid">
          <div className="hero_copy">
            {/* <div className="hero_pills">
              <Pill>Fast Onboarding</Pill>
              <Pill>Configurable Rules</Pill>
              <Pill>Compliant Ready Reports</Pill>
            </div> */}

            <h1 className="hero_title">
              {/* The compliance engine for{" "} */}
              <span className="hero_accent">
                Where Smart Onboarding Meets Compliance. Confidence at Every
                Checkpoint.
              </span>
            </h1>
            <p className="hero_subtitle">
              check² brings together identity verification and PEP & sanctions
              screening from trusted suppliers into a single smart workflow,
              delivering clear decisions and complete compliance reporting.
              {/* Run <strong>Experian CrossCore</strong> screening +{" "}
              <strong>Veriff</strong> identity verification in one clean
              workflow - with clear outcomes, overrides, and reporting. */}
            </p>

            <div className="hero_type">
              <span className="hero_typeLabel">Built for:</span>
              <span className="hero_typeValue">{typed}</span>
              <span className="caret" aria-hidden />
            </div>

            {/* <div className="hero_buttons">
              <button
                className="btn btn--primary btn--lg"
                onClick={() => scrollTo("cta")}
              >
                Launch checks
              </button>
              <button
                className="btn btn--ghost btn--lg"
                onClick={() => scrollTo("features")}
              >
                Explore features
              </button>
            </div> */}

            <div className="hero_trust">
              <div className="trust">
                <div className="trust_badge">Fast Onboarding</div>
                <div className="trust_badge">Configurable Rules</div>
                <div className="trust_badge">Compliant Ready Reports</div>
                {/* <div className="trust_badge">PDF reporting</div> */}
              </div>
            </div>

            <div className="hero_stats">
              <Stat value="Seconds" label="to create a session" />
              <Stat value="Configurable" label="decision rules" />
              <Stat value="One view" label="for the full journey" />
            </div>
          </div>

          {/* Hero Visual */}
          <div className="hero_visual">
            <div className="mock">
              <div className="mock_topbar">
                <div className="mock_dots">
                  <span />
                  <span />
                  <span />
                </div>
                <div className="mock_search">Search client…</div>
              </div>

              <div className="mock_content">
                <div className="mock_left">
                  <div className="kpi kpi--good">
                    <div className="kpi_label">IDV</div>
                    <div className="kpi_value">PASSED</div>
                    <div className="kpi_sub">Document verified</div>
                  </div>

                  <div className="kpi kpi--warn">
                    <div className="kpi_label">PEP / Sanctions</div>
                    <div className="kpi_value">REFERRED</div>
                    <div className="kpi_sub">2 potential matches</div>
                  </div>

                  <div className="kpi kpi--neutral">
                    <div className="kpi_label">Outcome</div>
                    <div className="kpi_value">DECLINED</div>
                    <div className="kpi_sub">Confirmed matches</div>
                  </div>
                </div>

                <div className="mock_right">
                  <div className="timeline">
                    <div className="timeline_title">Session lifecycle</div>
                    <div className="timeline_item done">
                      <span className="dot" />
                      <div>
                        <div className="t-title">Session created</div>
                        <div className="t-sub">API initiated</div>
                      </div>
                    </div>
                    <div className="timeline_item done">
                      <span className="dot" />
                      <div>
                        <div className="t-title">User started</div>
                        <div className="t-sub">Web SDK</div>
                      </div>
                    </div>
                    <div className="timeline_item active">
                      <span className="dot" />
                      <div>
                        <div className="t-title">Verification</div>
                        <div className="t-sub">In progress</div>
                      </div>
                    </div>
                    <div className="timeline_item">
                      <span className="dot" />
                      <div>
                        <div className="t-title">Decision</div>
                        <div className="t-sub">
                          Approved / Referred / Declined
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mini">
                    <div className="mini_head">Risk signals</div>
                    <div className="mini_row">
                      <span className="chip chip--green">Sanction Alert</span>
                    </div>
                    <div className="mini_row">
                      <span className="chip chip--yellow">PEP Match</span>
                    </div>
                    <div className="mini_row">
                      <span className="chip chip--blue">Mortality Fail</span>
                      <span className="chip chip--red">
                        Tampering Detected 
                      </span>
                    </div>
                  </div>
                  {/* <div className="mini-cards">

                    <div className="mini">
                      <div className="mini_head">Action</div>
                      <button
                        className="btn btn--tiny btn--primary"
                        onClick={() => scrollTo("cta")}
                      >
                        Create check
                      </button>
                      <button
                        className="btn btn--tiny btn--ghost"
                        onClick={() => scrollTo("how")}
                      >
                        View steps
                      </button>
                    </div>
                  </div> */}
                </div>
              </div>

              <FloatingChip style={{ top: "30%", left: "-16%" }}>
                🔒 HMAC verified webhooks
              </FloatingChip>
              <FloatingChip style={{ bottom: "18%", right: "-8%" }}>
                📄 Instant PDF export
              </FloatingChip>
              <FloatingChip style={{ top: "56%", right: "-10%" }}>
                ⚡ Real-time status updates
              </FloatingChip>
            </div>
          </div>
        </div>
      </main>

      {/* Features */}
      <section id="features" className="section">
        <div className="container">
          <div className="section_head">
            <h2 className="section_title">
              Everything you need for end-to-end checks
            </h2>
            <p className="section_sub">
              To One workflow. Simple for every user.
            </p>
          </div>

          <div className="grid grid--features">
            <FeatureCard
              icon="🧠"
              title="Decision logic that makes sense"
              desc="Clear outcomes with override reasoning, user-friendly handling, and rule-driven escalation."
            />
            <FeatureCard
              icon="🪪"
              title="Multiple Verification Journeys"
              desc="Choose standard or enhanced flows, featuring a branded user interface, SDK flexibility, and session tracking throughout."
            />
            <FeatureCard
              icon="🌍"
              title="Watchlist Screening"
              desc="Screen PEPs, sanctions, and mortality lists with clear match details and audit tracking."
            />
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="section section--alt">
        <div className="container how">
          <div className="how_left">
            <div className="section_head">
              <h2 className="section_title">How it works</h2>
              {/* <p className="section_sub">
                Built to fit real ops: fast session creation, strong screening,
                clean outcomes.
              </p> */}
            </div>

            <div className="steps">
              <Step
                idx="01"
                icon={<Step1 />}
                title="Create a check"
                desc="Enter client information to start the verification process - simple, fast, and secure."
              />
              <Step
                idx="02"
                icon={<Step2 />}
                title="Complete ID Verification"
                desc="Collect documents and run liveness checks via our secure link, with real-time session events."
              />
              <Step
                idx="03"
                icon={<Step3 />}
                title="Database Verification"
                desc="Perform simultaneous credit database checks and watchlist screening for a full risk view."
              />
              <Step
                idx="04"
                icon={<Step4 />}
                title="Review + Report"
                desc="Analyse outcomes to support risk decisions, then export a branded PDF in a single click."
              />
            </div>
          </div>

          {/* Optional: put an image in /public/images/flow.jpg and it will show */}
          <div className="how_right">
            {/* <div className="card diagram">
              <div className="diagram_head">
                <div className="diagram_title">Flow overview</div>
                <div className="diagram_hint">
                  Drop your diagram image into /public and link it here.
                </div>
              </div>

              <div className="diagram_imgWrap">
                <img
                  src="/images/api_flow_diagram.jpg"
                  alt="API flow diagram"
                  onError={(e) => {
                    // If the image isn't found, show a fallback block.
                    e.currentTarget.style.display = "none";
                    const fallback =
                      document.getElementById("diagram-fallback");
                    if (fallback) fallback.style.display = "block";
                  }}
                />
                <div id="diagram-fallback" className="diagram_fallback">
                  <div className="diagram_fallbackTitle">
                    No diagram image found
                  </div>
                  <div className="diagram_fallbackText">
                    Add a file at{" "}
                    <code>public/images/api_flow_diagram.jpg</code> (or update
                    the src).
                  </div>
                  <div className="diagram_fallbackPills">
                    <span className="chip chip--blue">Client App</span>
                    <span className="chip chip--yellow">Backend</span>
                    <span className="chip chip--green">Vendors</span>
                    <span className="chip chip--red">Decision</span>
                  </div>
                </div>
              </div>

              <div className="diagram_foot">
                <div className="diagram_note">
                  Tip: you can show both “API flow” + “session lifecycle”
                  diagrams here.
                </div>
              </div>
            </div> */}

            {/* <div className="card callout">
              <div className="callout_title">Admin-friendly by design</div>
              <div className="callout_desc">
                Built for review workflows: match resolution, reasons capture,
                and consistent decision logic.
              </div>
              <div className="callout_row">
                <span className="chip chip--green">APPROVED</span>
                <span className="chip chip--yellow">REFERRED</span>
                <span className="chip chip--red">DECLINED</span>
              </div>
            </div> */}

            <div className="callout-image-only">
              <Carousel items={slides} autoPlay interval={2500} />
            </div>
          </div>
        </div>
      </section>

      {/* Security */}
      {/* <section id="security" className="section">
        <div className="container security">
          <div className="security_card">
            <div className="security_icon">🛡️</div>
            <div className="security_content">
              <h3 className="security_title">Security-first integrations</h3>
              <p className="security_desc">
                Signed webhooks, controlled session creation, and audit logs
                that match compliance expectations.
              </p>
              <ul className="security_list">
                <li>Timing-safe signature verification</li>
                <li>Least-privilege API keys + environment separation</li>
                <li>Structured storage of results for consistent reporting</li>
              </ul>
            </div>
          </div>

          <div className="security_miniGrid">
            <div className="card miniStat">
              <div className="miniStat_top">Webhook Integrity</div>
              <div className="miniStat_big">HMAC</div>
              <div className="miniStat_sub">Verified before processing</div>
            </div>
            <div className="card miniStat">
              <div className="miniStat_top">Audit Trail</div>
              <div className="miniStat_big">Logged</div>
              <div className="miniStat_sub">Decisions + reason codes</div>
            </div>
            <div className="card miniStat">
              <div className="miniStat_top">Data Handling</div>
              <div className="miniStat_big">Scoped</div>
              <div className="miniStat_sub">Only what’s required</div>
            </div>
          </div>
        </div>
      </section> */}

      {/* CTA */}
      <section id="cta" className="cta">
        <div className="container cta_inner">
          <div className="cta_left">
            <h2 className="cta_title">
              Interested in learning more about how we can support you and your
              customers?
            </h2>
            <p className="cta_sub">
              Launch a new session, screen instantly, and export a clean report
              - all inside check².
            </p>

            <div className="cta_actions">
              <Link className="btn btn--primary btn--xl" to="/check-complete">
                View completion page
              </Link>
              <button className="btn btn--ghost btn--xl" onClick={() => scrollTo("how")}>
                See how it works
              </button>
            </div>

            <div className="cta_fine">
              <span className="chip chip--blue">Enhanced</span>
              <span className="chip chip--green">Standard</span>
              <span className="chip chip--yellow">PEP & SANCTIONS</span>
            </div>
          </div>

          <div className="cta_right">
            <div className="card ctaCard">
              <div className="ctaCard_title">How check² Works for You</div>
              <div className="ctaCard_list">
                <div className="ctaCard_item">
                  <span className="ctaCard_tick">✓</span> Accelerate your client
                  onboarding without compromising on compliance
                </div>
                <div className="ctaCard_item">
                  <span className="ctaCard_tick">✓</span> Deliver a frictionless
                  customer journey
                </div>
                <div className="ctaCard_item">
                  <span className="ctaCard_tick">✓</span> Benefit from a
                  innovative professionally hosted system
                </div>
                <div className="ctaCard_item">
                  <span className="ctaCard_tick">✓</span> check² is backed by
                  industry leading data sources 
                </div>
              </div>

              {/* <div className="ctaCard_footer">
                <div className="ctaCard_hint">
                  Plug into your backend routes & go.
                </div>
                <button className="btn btn--tiny btn--primary">
                  Open docs
                </button>
              </div> */}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container footer_inner">
          <div className="footer_left">
            <LogoSvg />
            {/* <div>
              <div className="footer_brand">check²</div>
              <div className="footer_muted">
                Compliance workflows, simplified.
              </div>
            </div> */}
          </div>

          <div className="footer_right">
            <button className="link" onClick={() => scrollTo("features")}>
              Features
            </button>
            <button className="link" onClick={() => scrollTo("how")}>
              How it works
            </button>
            <button className="link" onClick={() => scrollTo("cta")}>
              Get started
            </button>
            <div className="footer_copy">© {year} check²</div>
          </div>
        </div>
        <div className="container footer_legal ">
          <div className="footer_copy"></div>
          <div className="footer_copy">
            Rosemount Financial Solutions (IFA) Ltd is authorised and regulated
            by the Financial Conduct Authority (FCA), Firm Reference Number
            535515. Rosemount Financial Solutions (IFA) Ltd is registered in
            England and Wales (Company No: 05668905). Registered Office:
            Rosemount House, 2–4 Chequers Road, Basingstoke, United Kingdom,
            RG21 7PU.
          </div>
          <div className="footer_copy">
            check² is owned and operated by Rosemount Financial Solutions (IFA)
            Ltd and is provided as a verification tool only. It does not
            constitute financial advice or a regulated activity.
          </div>
          <div className="footer_copy">
            For details on how we process personal data, please see our{" "}
            <a
              href="https://rosemountfs-ifa.co.uk/PrivacyPolicy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </a>
            .
          </div>
        </div>
      </footer>
    </div>
  );
}
